import React, { useState } from 'react'
import { Table, Card, Button, Space, Typography, message, Spin, Empty, Alert, Collapse } from 'antd'
import { ShoppingCartOutlined, DownloadOutlined, WarningOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import { IImageryFeasibilityRecord, IWorkflowResult } from '../../OrganizationDetails/components/SitesTable/restUtils'
import { IMAGERY_FEASIBILITY_COLUMNS, ERROR_DISPLAY_LIMIT, ERROR_COLLAPSE_THRESHOLD } from '../constants'

const { Title, Text, Link } = Typography
const { Panel } = Collapse

interface IImageryFeasibilityProps {
  workflowResults: IWorkflowResult[]
  loading: boolean
  onPurchase: (recommendedImageRequestId: string, resourceVersionId: string) => void
  siteName?: string
}

const ImageryFeasibility: React.FC<IImageryFeasibilityProps> = ({
  workflowResults,
  loading,
  onPurchase,
  siteName,
}) => {
  const [purchasingRequestId, setPurchasingRequestId] = useState<string | null>(null)
  const [expandedErrors, setExpandedErrors] = useState<Record<string, { shapefile: boolean; pipeline: boolean }>>({})

  const toggleErrorExpansion = (requestId: string, errorType: 'shapefile' | 'pipeline') => {
    setExpandedErrors(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        [errorType]: !prev[requestId]?.[errorType]
      }
    }))
  }

  const handlePurchase = async (
    recommendedImageRequestId: string,
    resourceVersionId: string,
    requestId: string
  ) => {
    setPurchasingRequestId(requestId)
    try {
      await onPurchase(recommendedImageRequestId, resourceVersionId)
    } catch (error) {
      message.error('Failed to place order')
      console.error('Purchase error:', error)
    } finally {
      setPurchasingRequestId(null)
    }
  }

  const handleDownload = (
    imageryOrders: Array<{ cloudPath: string }>,
    imageryData: IImageryFeasibilityRecord[]
  ) => {
    if (!imageryOrders || imageryOrders.length === 0) {
      message.warning('No files available for download')
      return
    }
    
    message.info(
      `Starting download of ${imageryOrders.length} files. This may take a moment...`,
      5
    )
    
    imageryOrders.forEach((order, index) => {
      if (order.cloudPath) {
        setTimeout(() => {
          const iframe = document.createElement('iframe')
          iframe.style.display = 'none'
          iframe.style.visibility = 'hidden'
          iframe.style.width = '0'
          iframe.style.height = '0'
          iframe.src = order.cloudPath
          
          document.body.appendChild(iframe)
          
          setTimeout(() => {
            document.body.removeChild(iframe)
          }, 5000)
          
        }, index * 1000)
      }
    })

    const totalTime = imageryOrders.length * 1000
    setTimeout(() => {
      message.success(`All ${imageryOrders.length} downloads initiated! Check your Downloads folder.`)
    }, totalTime + 1000)
  }

  if (loading) {
    return (
      <Card style={{ marginTop: 24 }}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Loading imagery feasibility data...</Text>
          </div>
        </div>
      </Card>
    )
  }

  // Calculate total credits for each workflow
  const calculateTotalCredits = (data: IImageryFeasibilityRecord[]) => {
    return data.reduce((sum, item) => sum + item.credits, 0)
  }

  // Filter to only show workflows with dag_run_id (valid results)
  const validWorkflows = workflowResults.filter(
    (workflow) => workflow.metadata?.dag_run_id
  )

  if (!validWorkflows || validWorkflows.length === 0) {
    return (
      <Card style={{ marginTop: 24 }}>
        <Empty
          description="No imagery feasibility data available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    )
  }

  return (
    <Space direction="vertical" style={{ width: '100%', marginTop: 24 }} size="large">
      <Title level={4}>Imagery Feasibility Results</Title>
      {validWorkflows.map((workflow, index) => {
        const imageryFeasibilityData = workflow.result?.imageryFeasibility?.[0]
        const imageryData = imageryFeasibilityData?.records || []
        const imageryOrders = workflow.result?.imageryOrders || []
        const shapefileErrors = workflow.result?.shapefileErrors || []
        const pipelineErrors = workflow.result?.pipelineErrors || []
        const totalCredits = calculateTotalCredits(imageryData)
        const isPurchasing = purchasingRequestId === workflow.requestId
        const recommendedImageRequestId = imageryFeasibilityData?.recommendedImageRequestId || ''
        const hasOrders = imageryOrders.length > 0
        const hasErrors = shapefileErrors.length > 0 || pipelineErrors.length > 0

        return (
          <Card
            key={workflow.requestId}
            title={
              <Space>
                <Text strong>Request #{index + 1}</Text>
                <Text type="secondary">(ID: {workflow.requestId.substring(0, 8)}...)</Text>
                <Text type={workflow.status === 'COMPLETED' ? 'success' : 'warning'}>
                  {workflow.status}
                </Text>
                {hasOrders && (
                  <Text type="success">• {imageryOrders.length} file(s) ready</Text>
                )}
                {hasErrors && (
                  <Text type="warning">
                    <WarningOutlined /> Errors found
                  </Text>
                )}
              </Space>
            }
            extra={
              <Space>
                {hasOrders && (
                  <Button
                    type="default"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(imageryOrders, imageryData)}
                  >
                    Download ({imageryOrders.length})
                  </Button>
                )}
                {imageryData.length > 0 && recommendedImageRequestId && !hasOrders && (
                  <Button
                    type="primary"
                    icon={<ShoppingCartOutlined />}
                    onClick={() =>
                      handlePurchase(
                        recommendedImageRequestId,
                        workflow.resourceVersionId,
                        workflow.requestId
                      )
                    }
                    loading={isPurchasing}
                    disabled={isPurchasing}
                  >
                    {isPurchasing ? 'Processing...' : 'Purchase'}
                  </Button>
                )}
              </Space>
            }
          >
            {imageryData.length > 0 ? (
              <Table
                columns={IMAGERY_FEASIBILITY_COLUMNS}
                dataSource={imageryData.map((item, idx) => ({ ...item, key: idx }))}
                pagination={false}
                size="small"
                bordered
              />
            ) : (
              <Empty
                description={
                  workflow.result?.error
                    ? `Error: ${workflow.result.error}`
                    : 'No imagery feasibility data for this request'
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}

            {/* Display Errors Section */}
            {hasErrors && (
              <div style={{ marginTop: 16 }}>
                <Collapse
                  defaultActiveKey={[]}
                  expandIconPosition="end"
                  style={{ backgroundColor: '#fff' }}
                >
                  {shapefileErrors.length > 0 && (
                    <Panel
                      header={
                        <Space>
                          <WarningOutlined style={{ color: '#faad14' }} />
                          <Text strong>
                            Shapefile Errors ({shapefileErrors.length})
                          </Text>
                        </Space>
                      }
                      key="shapefile"
                    >
                      {(() => {
                        const shouldShowToggle = shapefileErrors.length > ERROR_COLLAPSE_THRESHOLD
                        const isExpanded = expandedErrors[workflow.requestId]?.shapefile
                        const displayErrors = shouldShowToggle && !isExpanded
                          ? shapefileErrors.slice(0, ERROR_DISPLAY_LIMIT)
                          : shapefileErrors

                        return (
                          <>
                            {displayErrors.map((error, idx) => (
                              <Alert
                                key={idx}
                                message={`Polygon ID: ${error.polygonId}`}
                                description={error.status}
                                type="warning"
                                showIcon
                                style={{ marginBottom: 8 }}
                              />
                            ))}
                            {shouldShowToggle && (
                              <Link
                                onClick={() => toggleErrorExpansion(workflow.requestId, 'shapefile')}
                                style={{ display: 'block', textAlign: 'center', marginTop: 8 }}
                              >
                                {isExpanded ? (
                                  <>
                                    <UpOutlined /> Show Less
                                  </>
                                ) : (
                                  <>
                                    <DownOutlined /> Show {shapefileErrors.length - ERROR_DISPLAY_LIMIT} More
                                  </>
                                )}
                              </Link>
                            )}
                          </>
                        )
                      })()}
                    </Panel>
                  )}

                  {pipelineErrors.length > 0 && (
                    <Panel
                      header={
                        <Space>
                          <WarningOutlined style={{ color: '#ff4d4f' }} />
                          <Text strong>
                            Pipeline Errors ({pipelineErrors.length})
                          </Text>
                        </Space>
                      }
                      key="pipeline"
                    >
                      {(() => {
                        // Filter out null/empty errors first
                        const validErrors = pipelineErrors.filter(
                          error => error.errorDescription || error.errorMessage
                        )
                        const shouldShowToggle = validErrors.length > ERROR_COLLAPSE_THRESHOLD
                        const isExpanded = expandedErrors[workflow.requestId]?.pipeline
                        const displayErrors = shouldShowToggle && !isExpanded
                          ? validErrors.slice(0, ERROR_DISPLAY_LIMIT)
                          : validErrors

                        return (
                          <>
                            {displayErrors.map((error, idx) => (
                              <Alert
                                key={idx}
                                message={error.title}
                                description={
                                  <div>
                                    {error.errorCode && <div><strong>Code:</strong> {error.errorCode}</div>}
                                    {error.errorDescription && <div><strong>Description:</strong> {error.errorDescription}</div>}
                                    {error.errorMessage && <div><strong>Message:</strong> {error.errorMessage}</div>}
                                  </div>
                                }
                                type="error"
                                showIcon
                                style={{ marginBottom: 8 }}
                              />
                            ))}
                            {shouldShowToggle && (
                              <Link
                                onClick={() => toggleErrorExpansion(workflow.requestId, 'pipeline')}
                                style={{ display: 'block', textAlign: 'center', marginTop: 8 }}
                              >
                                {isExpanded ? (
                                  <>
                                    <UpOutlined /> Show Less
                                  </>
                                ) : (
                                  <>
                                    <DownOutlined /> Show {validErrors.length - ERROR_DISPLAY_LIMIT} More
                                  </>
                                )}
                              </Link>
                            )}
                          </>
                        )
                      })()}
                    </Panel>
                  )}
                </Collapse>
              </div>
            )}
          </Card>
        )
      })}
    </Space>
  )
}

export default ImageryFeasibility

